import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, signal, ViewChild, inject } from '@angular/core';
import { SubscriptionPackagesComponent } from '../subscription-packages/subscription-packages.component';
import {
  CurrentSubscription,
  SubscriptionFrequency,
  SubscriptionPayload,
  SubscriptionPaymentRequest,
} from '../../../_interfaces/subscription.interface';
import { Subscription } from 'rxjs';
import { ReactiveFormsModule } from '@angular/forms';
import { SubscriptionApiService } from '../../../_services/subscription-service/api/subscription-api-service.service';
import { AsyncPipe } from '@angular/common';
import { environment } from '../../../../environments/environment';
import { Store } from '@ngxs/store';
import { FormFields } from '../../interfaces/project-interfaces';
import { BusinessStatus } from '../../../_interfaces/business.interface';
import { AppSelectors } from '../../../store/app/app.selectors';

@Component({
  selector: 'app-subscription-payment',
  imports: [SubscriptionPackagesComponent, ReactiveFormsModule, AsyncPipe],
  templateUrl: './subscription-payment.component.html',
})
export class SubscriptionPaymentComponent implements OnDestroy {
  readonly subscriptionApiService = inject(SubscriptionApiService);
  readonly store = inject(Store);
  private readonly cdr = inject(ChangeDetectorRef);
  readonly shouldShow$ = this.store.select(AppSelectors.shouldShowPaymentPendingOverlay);
  readonly subscriptions = new Subscription();

  @Input() status!: BusinessStatus;
  @ViewChild('form') form!: ElementRef;

  currentPackageId = signal<number>(0);
  currentFrequency = signal<SubscriptionFrequency>(SubscriptionFrequency.MONTHLY);
  selectionChanged = signal<boolean>(false);
  paymentRequested = signal<boolean>(false);
  currentPackage!: SubscriptionPayload;
  paymentUrl!: string;
  formFields: FormFields[] = [];
  readonly dashUrl = environment.dashboardBaseUrl;

  constructor() {
    this.getCurrentPackage();
  }

  // Get package selected on onboarding flow, to highlight.
  getCurrentPackage() {
    this.subscriptions.add(
      this.subscriptionApiService.getCurrentSubscription().subscribe({
        next: (data: CurrentSubscription) => {
          if (data) {
            this.currentPackage = {
              package: data.package.id,
              frequency: data.frequency,
            };
            this.currentPackageId.set(Number(data.package.id));
            this.currentFrequency.set(
              data.package.monthly_cost ? SubscriptionFrequency.MONTHLY : SubscriptionFrequency.YEARLY,
            );
          }
        },
      }),
    );
  }

  onSelectPackage(data: SubscriptionPayload) {
    this.selectionChanged.set(true);
    this.currentPackageId.set(data.package);
    this.currentFrequency.set(data.frequency);
  }

  onFrequencyChange(data: Partial<SubscriptionPayload>) {
    this.selectionChanged.set(true);
    if (data.frequency) this.currentFrequency.set(data.frequency);
  }

  requestSubscriptionPaymentDetails() {
    const data: SubscriptionPaymentRequest = {
      return_url: this.dashUrl + 'dashboard?payment=success',
      cancel_url: this.dashUrl + 'dashboard?payment=error',
    };
    if (this.selectionChanged()) {
      data.package = this.currentPackageId();
      data.frequency = this.currentFrequency();
    }
    this.subscriptionApiService.requestSubscriptionPaymentDetails(data).subscribe({
      next: (data) => {
        if (data) {
          this.paymentUrl = data.payment_form_url;
          const fields = data.payment_form_data as FormFields;
          this.formFields = Object.entries(fields).map(([key, value]) => ({
            name: key,
            value: value,
          }));
          this.cdr.detectChanges();
        }
      },
      complete: () => {
        if (this.formFields.length > 0) {
          this.paymentRequested.set(true);
          this.form.nativeElement.submit();
        }
      },
    });
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  protected readonly BusinessStatus = BusinessStatus;
}
